import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { ModalShell, ModalActions } from './ModalShell';
// ═══════════════════════════════════════════════════════════════
// FILE: src/components/shared/ConfirmProvider.jsx
// #1912 -- real replacement for native window.confirm() popups.
// window.confirm() is a browser-native, unstyled modal that reads
// as jarring and off-brand next to the rest of Charlie's UI. This
// is a single shared provider + hook so every call site gets
// identical, on-brand behavior instead of 30 independent patches --
// Ranch Rule: shared infrastructure, not duplicated per file.
//
// Usage (drop-in replacement for window.confirm):
//   const confirm = useConfirm();
//   if (!(await confirm('Detach this partner?'))) return;
//
// Or with more control:
//   const ok = await confirm({
//     title: 'Delete company',
//     message: 'This removes both records permanently.',
//     confirmLabel: 'Delete',
//     danger: true,
//   });
// ═══════════════════════════════════════════════════════════════

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((arg) => {
    const opts = typeof arg === 'string' ? { message: arg } : (arg || {});
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setRequest({
        title: opts.title || 'Are you sure?',
        message: opts.message || '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        danger: opts.danger || false,
      });
    });
  }, []);

  const settle = useCallback((result) => {
    setRequest(null);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request && (
        <ModalShell title={request.title} onClose={() => settle(false)} width={420} branded>
          {request.message && (
            <div style={{ fontSize: 14, color: 'var(--gov-slate-700)', lineHeight: 1.5 }}>{request.message}</div>
          )}
          <ModalActions
            onCancel={() => settle(false)}
            onConfirm={() => settle(true)}
            confirmLabel={request.confirmLabel}
            danger={request.danger}
          />
        </ModalShell>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm() must be called within a ConfirmProvider (mounted once at app root)');
  }
  return ctx;
}
